import { useEffect, useRef, useState } from 'react';
import { usePlay, type PlayApi } from '../play/PlayController';
import type { Description, Observation } from '../protocol/ui';
import type { PackageUiRef } from '../protocol/wire';
import type { PresentationV2 } from '../presentation/types';
import type { Draft, StepValue } from '../model/choices';

export interface PackageUiView {
  observation: Observation; description: Description | null; seat: string | null;
  revision: number; readOnly: boolean; pending: boolean;
}
export interface PackageUiApi {
  apiVersion: 'ui-sdk-1';
  choose(id: string): void;
  submitDraft(id: string, draft: Draft): void;
  answer(value: StepValue): void; confirm(): void; cancel(): void;
  inspect(id: string | null): void;
}
export interface PackageUiInstance { update(view: PackageUiView): void; destroy(): void }
export interface PackageUiModule {
  apiVersion: 'ui-sdk-1';
  mount(root: ShadowRoot, api: PackageUiApi): PackageUiInstance | Promise<PackageUiInstance>;
}

/** Loads only a host-authorized, content-pinned same-origin module. This is trusted code, not a sandbox. */
export async function importPackageUi(ref: PackageUiRef): Promise<PackageUiModule> {
  const url = new URL(ref.url, window.location.href);
  if (ref.api !== 'ui-sdk-1' || url.origin !== window.location.origin || !/^\/presentation\/[a-f0-9]{64}\/[A-Za-z0-9_.-]+\.mjs$/.test(url.pathname) || url.search || url.hash)
    throw new Error('Invalid package UI origin or API.');
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('Package UI unavailable.');
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await response.arrayBuffer())), b => b.toString(16).padStart(2, '0')).join('');
  if (hash !== ref.sha256) throw new Error('Package UI integrity mismatch.');
  const module = await import(/* @vite-ignore */ url.href) as PackageUiModule;
  if (module.apiVersion !== 'ui-sdk-1' || typeof module.mount !== 'function') throw new Error('Unsupported package UI exports.');
  return module;
}

export function packageUiApi(current: () => PlayApi | null): PackageUiApi {
  const available = () => { const p = current(); return p && !p.readOnly && !p.pending ? p : null; };
  return {
    apiVersion: 'ui-sdk-1',
    choose: id => { const p = available(); const c = p?.choices?.find(c => c.id === id); if (p && c) p.chooseEntry(`package:${id}`, { choiceId: c.id, label: c.label, group: c.group ?? '', via: 'rule' }); },
    submitDraft: (id, draft) => { const p = available(); const c = p?.choices?.find(c => c.id === id); if (p && c) p.submitDraft(`package:${id}`, c, draft); },
    answer: value => available()?.answerStep(value), confirm: () => available()?.confirm(), cancel: () => available()?.cancel(),
    inspect: id => current()?.inspect(id),
  };
}

export function PackageUiHost({ presentation, observation, description, seat, revision }: {
  presentation: PresentationV2; observation: Observation; description: Description | null; seat: string | null; revision: number;
}) {
  const play = usePlay(); const latest = useRef(play); latest.current = play;
  const view = useRef<PackageUiView>({ observation, description, seat, revision, readOnly: true, pending: false });
  view.current = { observation, description, seat, revision, readOnly: play?.readOnly ?? true, pending: play?.pending ?? false };
  const host = useRef<HTMLDivElement>(null); const mounted = useRef<PackageUiInstance | null>(null);
  const active = useRef({ alive: false });
  const [notice, setNotice] = useState<string | null>(null);
  const ref = presentation.module;
  useEffect(() => {
    if (!ref || !host.current) return;
    let disposed = false; const guard = { alive: true }; active.current = guard;
    const element = document.createElement('div'); host.current.replaceChildren(element);
    const shadow = element.attachShadow({ mode: 'open' });
    const fail = () => { if (!disposed) { disposed = true; guard.alive = false; try { mounted.current?.destroy(); } catch { /* Fallback remains available. */ } mounted.current = null; setNotice('Custom view unavailable. Use the table controls.'); element.remove(); } };
    const timer = window.setTimeout(fail, 10000);
    setNotice(null);
    void importPackageUi(ref).then(async module => {
      if (disposed) return;
      const instance = await module.mount(shadow, packageUiApi(() => disposed || !guard.alive ? null : latest.current));
      if (!instance || typeof instance.update !== 'function' || typeof instance.destroy !== 'function') throw new Error('Invalid module instance.');
      if (disposed) { instance.destroy(); return; }
      window.clearTimeout(timer); mounted.current = instance; instance.update(structuredClone(view.current));
    }).catch(fail).finally(() => window.clearTimeout(timer));
    return () => { disposed = true; guard.alive = false; window.clearTimeout(timer); try { mounted.current?.destroy(); } catch { /* Generic controls stay mounted. */ } mounted.current = null; element.remove(); };
  }, [ref?.url, ref?.sha256, seat]);
  useEffect(() => {
    try { mounted.current?.update(structuredClone(view.current)); }
    catch { active.current.alive = false; try { mounted.current?.destroy(); } catch { /* Continue with generic controls. */ } mounted.current = null; host.current?.replaceChildren(); setNotice('Custom view unavailable. Use the table controls.'); }
  }, [observation, description, seat, revision, play]);
  return ref ? <section aria-label="Package view"><div ref={host} />{notice && <p role="status">{notice}</p>}</section> : null;
}
